import React, { useEffect, useState } from "react";
import api, { formatApiError, API, getAccessToken } from "@/lib/api";
import { useT } from "@/i18n";
import { useCenter } from "@/context/CenterContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, FileText, Send, DollarSign, Bell, Trash2, Download, Repeat, X } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = { draft: "bg-slate-500/15 text-slate-600", sent: "bg-blue-500/15 text-blue-600",
  paid: "bg-emerald-500/15 text-emerald-600", overdue: "bg-destructive/15 text-destructive", cancelled: "bg-amber-500/15 text-amber-600" };

const pdfUrl = (id) => `${API}/billing/invoices/${id}/pdf?_t=${getAccessToken()}`;

function InvoiceDialog({ open, onOpenChange, invoice, centers, onSaved }) {
  const { t } = useT();
  const [form, setForm] = useState({});
  const today = new Date().toISOString().slice(0, 10);
  useEffect(() => {
    if (open) setForm(invoice ? { ...invoice } : { center_id: centers[0]?.id || "", issue_date: today, due_date: today, line_items: [{ description: "", quantity: 1, unit_price: 0 }], tax_rate: 14.975, currency: "CAD", notes: "", recurring: false, recurrence: "monthly" });
  }, [open, invoice]); // eslint-disable-line
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setItem = (i, k, v) => { const li = [...form.line_items]; li[i][k] = k === "description" ? v : parseFloat(v || 0); set("line_items", li); };
  const subtotal = (form.line_items || []).reduce((a, li) => a + (li.quantity || 0) * (li.unit_price || 0), 0);
  const total = subtotal * (1 + (form.tax_rate || 0) / 100);
  const save = async () => {
    try {
      if (invoice) await api.put(`/billing/invoices/${invoice.id}`, form); else await api.post("/billing/invoices", form);
      toast.success(t("save")); onSaved(); onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto scrollbar-thin">
        <DialogHeader><DialogTitle>{invoice ? t("editInvoice") : t("newInvoice")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {!invoice && (
            <div className="space-y-1.5"><Label className="text-xs">{t("center")}</Label>
              <Select value={form.center_id} onValueChange={(v) => set("center_id", v)}>
                <SelectTrigger data-testid="invoice-center"><SelectValue /></SelectTrigger>
                <SelectContent>{centers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5"><Label className="text-xs">{t("issueDate")}</Label><Input type="date" value={form.issue_date || ""} onChange={(e) => set("issue_date", e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="text-xs">{t("dueDate")}</Label><Input type="date" value={form.due_date || ""} onChange={(e) => set("due_date", e.target.value)} data-testid="invoice-due" /></div>
          </div>
          <Label className="text-xs">{t("lineItems")}</Label>
          {(form.line_items || []).map((li, i) => (
            <div key={i} className="flex gap-2 items-center">
              <Input className="flex-1" placeholder={t("description")} value={li.description} onChange={(e) => setItem(i, "description", e.target.value)} data-testid="invoice-item-desc" />
              <Input className="w-16" type="number" value={li.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} />
              <Input className="w-24" type="number" value={li.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} />
              <Button variant="ghost" size="icon" onClick={() => set("line_items", form.line_items.filter((_, j) => j !== i))}><X className="w-4 h-4" /></Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => set("line_items", [...form.line_items, { description: "", quantity: 1, unit_price: 0 }])} data-testid="add-line-item"><Plus className="w-4 h-4 mr-1" />{t("add")}</Button>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5"><Label className="text-xs">{t("taxRate")}</Label><Input type="number" value={form.tax_rate} onChange={(e) => set("tax_rate", parseFloat(e.target.value || 0))} /></div>
            <div className="flex items-end"><p className="text-sm">{t("total")}: <b>{total.toFixed(2)} {form.currency}</b></p></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">{t("notes")}</Label><Textarea rows={2} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} /></div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="rec" checked={!!form.recurring} onChange={(e) => set("recurring", e.target.checked)} className="w-4 h-4" data-testid="invoice-recurring" />
            <Label htmlFor="rec">{t("recurringInvoice")}</Label>
            {form.recurring && (
              <Select value={form.recurrence} onValueChange={(v) => set("recurrence", v)}>
                <SelectTrigger className="w-40 h-8 ml-2"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="monthly">{t("monthly")}</SelectItem><SelectItem value="quarterly">{t("quarterly")}</SelectItem><SelectItem value="yearly">{t("yearly")}</SelectItem></SelectContent>
              </Select>
            )}
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>{t("cancel")}</Button><Button onClick={save} data-testid="save-invoice">{t("save")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Billing() {
  const { t } = useT();
  const { isSuperAdmin, activeId, centers } = useCenter();
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [dialog, setDialog] = useState({ open: false, invoice: null });
  const [pay, setPay] = useState({ open: false, invoice: null, amount: "", method: "bank_transfer" });

  const load = async () => {
    const params = isSuperAdmin ? {} : { center_id: activeId };
    const { data } = await api.get("/billing/invoices", { params });
    setInvoices(data);
    if (isSuperAdmin) api.get("/billing/summary").then((r) => setSummary(r.data));
  };
  useEffect(() => { load(); }, [activeId]); // eslint-disable-line

  const send = async (id) => { await api.post(`/billing/invoices/${id}/send`); toast.success(t("send")); load(); };
  const remind = async (id) => { await api.post(`/billing/invoices/${id}/remind`); toast.success(t("reminder")); };
  const del = async (id) => { if (!window.confirm(t("delete") + "?")) return; await api.delete(`/billing/invoices/${id}`); load(); };
  const recordPay = async () => {
    try { await api.post(`/billing/invoices/${pay.invoice.id}/payments`, { amount: parseFloat(pay.amount), method: pay.method }); toast.success(t("recordPayment")); setPay({ open: false, invoice: null, amount: "", method: "bank_transfer" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl font-bold tracking-tight">{t("billing")}</h1>
        {isSuperAdmin && <Button onClick={() => setDialog({ open: true, invoice: null })} data-testid="add-invoice-button"><Plus className="w-4 h-4 mr-1.5" />{t("newInvoice")}</Button>}
      </div>

      {isSuperAdmin && summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card className="p-4"><p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{t("revenue")}</p><p className="font-display text-2xl font-extrabold mt-1 text-emerald-600">${summary.paid.toLocaleString()}</p></Card>
          <Card className="p-4"><p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{t("outstanding")}</p><p className="font-display text-2xl font-extrabold mt-1">${summary.outstanding.toLocaleString()}</p></Card>
          <Card className="p-4"><p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{t("overdue")}</p><p className="font-display text-2xl font-extrabold mt-1 text-destructive">{summary.overdue}</p></Card>
          <Card className="p-4"><p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{t("invoices")}</p><p className="font-display text-2xl font-extrabold mt-1">{summary.total_invoices}</p></Card>
        </div>
      )}

      <Card className="divide-y divide-border">
        {invoices.length === 0 && <p className="p-8 text-center text-muted-foreground">{t("noData")}</p>}
        {invoices.map((inv) => (
          <div key={inv.id} className="flex flex-wrap items-center gap-3 p-4" data-testid="invoice-row">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center"><FileText className="w-5 h-5 text-primary" /></div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold">{inv.number}</p>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[inv.status]}`}>{t(inv.status)}</span>
                {inv.recurring && <Badge variant="secondary" className="text-[10px]"><Repeat className="w-3 h-3 mr-1" />{t(inv.recurrence)}</Badge>}
              </div>
              <p className="text-xs text-muted-foreground">{inv.center_name} · {t("dueDate")} {inv.due_date}</p>
            </div>
            <div className="text-right"><p className="font-display font-bold">{inv.total.toFixed(2)} {inv.currency}</p>{inv.amount_paid > 0 && inv.status !== "paid" && <p className="text-[11px] text-muted-foreground">{t("paid")}: {inv.amount_paid.toFixed(2)}</p>}</div>
            <div className="flex gap-1">
              <a href={pdfUrl(inv.id)} target="_blank" rel="noreferrer"><Button size="icon" variant="ghost" data-testid="invoice-pdf"><Download className="w-4 h-4" /></Button></a>
              {isSuperAdmin && inv.status === "draft" && <Button size="icon" variant="ghost" onClick={() => send(inv.id)} data-testid="send-invoice"><Send className="w-4 h-4" /></Button>}
              {isSuperAdmin && ["sent", "overdue"].includes(inv.status) && <Button size="icon" variant="ghost" onClick={() => setPay({ open: true, invoice: inv, amount: (inv.total - inv.amount_paid).toFixed(2), method: "bank_transfer" })} data-testid="record-payment"><DollarSign className="w-4 h-4 text-emerald-600" /></Button>}
              {isSuperAdmin && inv.status === "overdue" && <Button size="icon" variant="ghost" onClick={() => remind(inv.id)} data-testid="remind-invoice"><Bell className="w-4 h-4 text-amber-600" /></Button>}
              {isSuperAdmin && <Button size="icon" variant="ghost" onClick={() => setDialog({ open: true, invoice: inv })}>{t("edit").slice(0, 1)}</Button>}
              {isSuperAdmin && <Button size="icon" variant="ghost" onClick={() => del(inv.id)} data-testid="delete-invoice"><Trash2 className="w-4 h-4 text-destructive" /></Button>}
            </div>
          </div>
        ))}
      </Card>

      <InvoiceDialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} invoice={dialog.invoice} centers={centers || []} onSaved={load} />

      <Dialog open={pay.open} onOpenChange={(o) => setPay((p) => ({ ...p, open: o }))}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t("recordPayment")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs">{t("amount")}</Label><Input type="number" value={pay.amount} onChange={(e) => setPay((p) => ({ ...p, amount: e.target.value }))} data-testid="payment-amount" /></div>
            <div className="space-y-1.5"><Label className="text-xs">{t("paymentMethod")}</Label>
              <Select value={pay.method} onValueChange={(v) => setPay((p) => ({ ...p, method: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="bank_transfer">{t("bankTransfer")}</SelectItem><SelectItem value="credit_card">{t("creditCard")}</SelectItem><SelectItem value="cheque">{t("cheque")}</SelectItem><SelectItem value="cash">{t("cash")}</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setPay({ open: false, invoice: null, amount: "", method: "bank_transfer" })}>{t("cancel")}</Button><Button onClick={recordPay} data-testid="submit-payment">{t("confirm")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
